import { debounce } from "lodash";
import { client } from "@/shared/api/pref-storage";
import { ONBOARDING_STATE_KEY, type OnboardingState } from "./types";
import { isAxiosError } from "axios";

const endpoint = `/preferences/user/${ONBOARDING_STATE_KEY}`;

/**
 * Fetch the user's onboarding state. A 404 means the key has never been set
 * (first ever login) and resolves to null. The pref-storage response
 * interceptor is already configured to skip Sentry for this expected 404.
 */
export async function getOnboardingState(): Promise<OnboardingState | null> {
  try {
    const { data } = await client.get(endpoint);
    return (data?.value as OnboardingState) ?? null;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function saveOnboardingState(state: OnboardingState) {
  return client.patch(endpoint, { value: state });
}

/** Dev/testing: wipe the saved state so the flow re-triggers as first-run. */
export async function resetOnboardingState() {
  return client.patch(endpoint, { value: null });
}

/**
 * Debounced variant for rapid successive updates (checklist ticks, step
 * navigation). Leading edge so the first write (startedAt) lands immediately.
 */
export const saveOnboardingStateDebounced = debounce(
  saveOnboardingState,
  2 * 1000,
  { leading: true }
);
